import React, { useState } from "react";
import CallIcon from "@mui/icons-material/Call";
import PlaceIcon from "@mui/icons-material/Place";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
  CssBaseline,
  Menu,
  MenuItem,
  Button,
  Box,
} from "@mui/material";
import { Link } from "react-scroll";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import crescent from "../Images/Crescent.png";
import useDisableCopyPaste from "./useDisableCopyPaste";
import { colors, fonts, ui } from "../theme";

function Navigation() {
  useDisableCopyPaste();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setDrawerOpen(false);
  };

  const navLinks = [
    { to: "home-section", label: "Home" },
    { to: "about-section", label: "About" },
    { to: "programs-section", label: "Programs" },
    { to: "accreditation-section", label: "Accreditation" },
    { to: "impact-section", label: "Impact" },
  ];

  const submenuLinks = [
    { to: "portfolio-section", label: "Portfolio & Pillars" },
    { to: "framework-section", label: "Excellence Framework" },
    { to: "departments-section", label: "Department Impact" },
    { to: "training-section", label: "Internship Tracks" },
    { to: "projects-section", label: "Projects & Publication" },
    { to: "comparison-section", label: "Why Partner" },
    { to: "roadmap-section", label: "Roadmap" },
    { to: "campus-section", label: "Institutions" },
  ];

  const handleSubMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleSubMenuClose = () => setAnchorEl(null);

  const linkBase = {
    cursor: "pointer",
    color: colors.navy,
    fontFamily: fonts.body,
    fontWeight: 600,
    fontSize: "0.95rem",
    textDecoration: "none",
    padding: "6px 0",
  };

  const renderNavLinks = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
      {navLinks.map((link, index) => (
        <Link
          key={index}
          to={link.to}
          duration={800}
          className="nav-link"
          style={linkBase}
          spy={true}
          smooth={true}
          offset={-70}
          onClick={() => setDrawerOpen(false)}
        >
          {link.label}
        </Link>
      ))}
      <Box
        onClick={handleSubMenuOpen}
        sx={{ ...linkBase, display: "inline-flex", alignItems: "center" }}
      >
        More <ArrowDropDownIcon fontSize="small" />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSubMenuClose}
        MenuListProps={{ style: { backgroundColor: colors.white } }}
      >
        {submenuLinks.map((link, index) => (
          <Link
            key={index}
            to={link.to}
            duration={800}
            spy={true}
            smooth={true}
            offset={-70}
            style={{ textDecoration: "none" }}
            onClick={() => {
              setDrawerOpen(false);
              handleSubMenuClose();
            }}
          >
            <MenuItem sx={{ color: colors.navy, fontWeight: 500, fontFamily: fonts.body }}>
              {link.label}
            </MenuItem>
          </Link>
        ))}
      </Menu>
      <Link to="contacts-section" smooth={true} duration={800} offset={-70}>
        <Button variant="contained" disableElevation sx={{ ...ui.ctaButton, px: 2.5 }}>
          Partner With Us
        </Button>
      </Link>
    </Box>
  );

  // Compact contact info that now lives inside the navbar row (desktop only).
  const contactMeta = [
    { icon: <CallIcon sx={{ fontSize: 16, color: colors.blueDark }} />, label: "+91 86670 02959" },
    { icon: <PlaceIcon sx={{ fontSize: 16, color: colors.pinkDark }} />, label: "Salem, Tamil Nadu" },
  ];

  const renderContactMeta = () => (
    <Box
      sx={{
        display: { xs: "none", lg: "flex" },
        alignItems: "center",
        gap: 2,
        mr: 1,
        pr: 2.5,
        borderRight: `1px solid ${colors.border}`,
      }}
    >
      {contactMeta.map((c) => (
        <Box
          key={c.label}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            color: colors.textMuted,
            fontFamily: fonts.body,
            fontWeight: 600,
            fontSize: "0.82rem",
            transition: "color .2s ease",
            "&:hover": { color: colors.navy },
          }}
        >
          {c.icon}
          {c.label}
        </Box>
      ))}
    </Box>
  );

  const renderDrawerLinks = () => (
    <List>
      {[...navLinks, ...submenuLinks].map((link, index) => (
        <Link
          key={index}
          to={link.to}
          duration={800}
          spy={true}
          smooth={true}
          offset={-70}
          onClick={() => setDrawerOpen(false)}
          style={{ textDecoration: "none" }}
        >
          <ListItem button sx={{ color: colors.white, cursor: "pointer" }}>
            <ListItemText
              primary={link.label}
              primaryTypographyProps={{ fontFamily: fonts.body, fontWeight: 500 }}
            />
          </ListItem>
        </Link>
      ))}
      <Box sx={{ px: 2, pt: 2 }}>
        <Link to="contacts-section" smooth={true} duration={800} offset={-70} onClick={() => setDrawerOpen(false)}>
          <Button fullWidth variant="contained" disableElevation sx={ui.ctaButton}>
            Partner With Us
          </Button>
        </Link>
      </Box>
    </List>
  );

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        className="custom-navbar"
        elevation={0}
        sx={{
          backgroundColor: colors.white,
          borderBottom: "1px solid #e6edf7",
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 78 } }}>
            <Box sx={{ flexGrow: 1 }}>
              <img
                src={crescent}
                onClick={scrollToTop}
                style={{
                  cursor: "pointer",
                  maxHeight: 64,
                  transition: "transform .25s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                alt="Crescent EdTech"
              />
            </Box>

            {isMobile ? (
              <IconButton
                edge="start"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ color: colors.navy }}
              >
                {drawerOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {renderContactMeta()}
                {renderNavLinks()}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { backgroundColor: colors.navy, width: 260 } }}
      >
        <Box sx={{ width: 260, pt: "90px" }}>{renderDrawerLinks()}</Box>
      </Drawer>

      {/* Spacer to offset the fixed AppBar */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 78 } }} />
    </>
  );
}

export default Navigation;
